// Display helpers for the My Team roster / My Supervisor card.

/** @type {Record<string, {label: string, cls: string}>} */
const SEAT = {
  active: { label: 'Active', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  trial: { label: 'Trial', cls: 'bg-sky-500/15 text-sky-300 border-sky-500/30' },
  expiring: { label: 'Expiring Soon', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  expired: { label: 'Expired', cls: 'bg-rose-500/15 text-rose-300 border-rose-500/30' },
};

/**
 * @param {{state?: string}} item Roster row from GET /team/downline.
 * @returns {{label: string, cls: string}} Status badge.
 */
export const seatBadge = (item) => SEAT[item?.state] || SEAT.active;

/**
 * @param {{expiresAt?: string|null, daysLeft?: number|null}} item
 * @returns {string} "Expires on 12-15-2026 (in 14 days)" / "Expired 3 days ago" / "No expiry".
 */
export function expiryText(item) {
  if (!item?.expiresAt) return 'No expiry set';
  const d = new Date(item.expiresAt);
  const date = `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}-${d.getFullYear()}`;
  const n = item.daysLeft;
  if (n == null) return `Expires on ${date}`;
  if (n <= 0) return n === 0 ? `Expired today (${date})` : `Expired ${Math.abs(n)} day${Math.abs(n) === 1 ? '' : 's'} ago (${date})`;
  return `Expires on ${date} (in ${n} day${n === 1 ? '' : 's'})`;
}

/**
 * @param {{depth?: number, uplineName?: string}} item
 * @returns {string} "Direct (L1)" or "L2 via John Doe".
 */
export function depthLabel(item) {
  const depth = item?.depth || 1;
  if (depth <= 1) return 'Direct (L1)';
  return `L${depth}${item?.uplineName ? ` via ${item.uplineName}` : ''}`;
}

/**
 * @param {boolean} next The new Team Admin value being requested.
 * @param {string} name
 * @returns {string} Confirmation copy for the toggle.
 */
export const teamAdminPrompt = (next, name) => (next
  ? 'Are you sure you want to make this agent a team admin? They will be able to add other users to your Team on your behalf.'
  : `Revoke Team Admin privileges from ${name || 'this agent'}? They will no longer be able to add users to your Team.`);

/**
 * @param {boolean} next New "can add own team" value.
 * @param {string} name
 * @returns {string} Confirmation copy for the own-team switch.
 */
export const ownTeamPrompt = (next, name) => (next
  ? `Allow ${name || 'this agent'} to add agents under their own team? New agents they invite will report to them and will not need extra approval.`
  : `Stop ${name || 'this agent'} from adding agents under their own team? Existing members stay where they are.`);

/**
 * @param {Array<{value: string, label: string}>} options From GET /team/invite-options.
 * @returns {string} Default placement ("me" when allowed, otherwise the only option).
 */
export const defaultPlacement = (options) => (options?.some((o) => o.value === 'me') ? 'me' : options?.[0]?.value || 'me');

/**
 * @param {string} name Supervisor name.
 * @returns {string} Disconnect confirmation copy.
 */
export const disconnectPrompt = (name) => `Are you sure you want to disconnect from your supervisor ${name}? You will become an independent team lead and will no longer inherit their shared team workflows.`;
